import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import useCompanyService from "../../database/CompanyService";
import { RootState } from "../../app/store";
import useProductService from "../../database/ProductService";
import useVoucherService from "../../database/VoucherService";
import useProductKeyService from "../../database/ProductKeyService";
import useUserService from "../../database/UserService";
import { ProductSummary } from "./models/ProductSummary";
import { VoucherLookupResult } from "./models/VoucherLookupResult";
import { Company } from "../../models/Company";
import { Product } from "../../models/Product";
import { User } from "../../models/User";
import { ProductKey } from "../../models/ProductKey";

export interface ReportingPageState {
    isLoading: boolean;
    summaries: ProductSummary[];
    loadError: string | null;

    voucherSearchKey: string;
    isLookupLoading: boolean;
    lookupResult: VoucherLookupResult | null;
    lookupError: string | null;
}

const initialState: ReportingPageState = {
    isLoading: false,
    summaries: [],
    loadError: null,

    voucherSearchKey: "",
    isLookupLoading: false,
    lookupResult: null,
    lookupError: null,
}

export const ReportingPageSlice = createSlice({
    name: 'reportingPage',
    initialState,
    reducers: {
        setLoadingState: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setSummaries: (state, action: PayloadAction<ProductSummary[]>) => {
            state.summaries = action.payload;
        },
        setLoadError: (state, action: PayloadAction<string | null>) => {
            state.loadError = action.payload;
        },
        setVoucherSearchKey: (state, action: PayloadAction<string>) => {
            state.voucherSearchKey = action.payload;
        },
        setLookupLoadingState: (state, action: PayloadAction<boolean>) => {
            state.isLookupLoading = action.payload;
        },
        setLookupResult: (state, action: PayloadAction<VoucherLookupResult | null>) => {
            state.lookupResult = action.payload;
        },
        setLookupError: (state, action: PayloadAction<string | null>) => {
            state.lookupError = action.payload;
        },
    }
});

export const reportingPageInitialLoadAsync = createAsyncThunk<void, void, { state: RootState }>(
    'reportingPage/initialLoadAsync',
    async (_, { dispatch, getState, rejectWithValue }) => {

        dispatch(setLoadingState(true));
        dispatch(setLoadError(null));

        /* Without this the page sits on the spinner forever when a read fails,
           which is exactly what a signed-out or unauthorised session gets. */
        try {
            var companyService = useCompanyService();
            var productService = useProductService();
            var voucherService = useVoucherService();
            var productKeyService = useProductKeyService();

            var companies: Company[] = await companyService.getCompaniesAsync();

            var summaries: ProductSummary[] = [];

            for (var company of companies) {

                var products = await productService.getProductsByCompanyGuid(company.guid);

                for (var product of products) {
                    var totalVoucherCount = await voucherService.getVoucherCountByCompanyAndProductAsync(company.guid, product.guid);
                    var unassignedVoucherCount = await voucherService.getUnassignedVoucherCountAsync(company.guid, product.guid);
                    var unexportedVoucherCount = await voucherService.getUnexportedVoucherCountAsync(company.guid, product.guid);
                    var redeemedVoucherCount = totalVoucherCount - unassignedVoucherCount;

                    var totalProductKeyCount = await productKeyService.getProductKeyCountByCompanyAndProductAsync(company.guid, product.guid);
                    var unassignedProductKeyCount = await productKeyService.getUnassignedProductKeyCountAsync(company.guid, product.guid);
                    var redeemableProductKeyCount = await productKeyService.getRedeemableProductKeyCountAsync(company.guid, product.guid);

                    var newSummary = new ProductSummary(company.name, product.name, totalVoucherCount, unassignedVoucherCount, unexportedVoucherCount, redeemedVoucherCount,
                        totalProductKeyCount, unassignedProductKeyCount, redeemableProductKeyCount);
                    summaries = [...summaries, newSummary];
                }
            }
            dispatch(setSummaries(summaries));
        } catch (error) {
            dispatch(setLoadError(`Could not load the report: ${error}`));
        }

        dispatch(setLoadingState(false));
        return;
    }
);

export const voucherLookupAsync = createAsyncThunk<void, void, { state: RootState }>(
    'reportingPage/voucherLookupAsync',
    async (_, { dispatch, getState, rejectWithValue }) => {

        const state = getState().reportingPage;

        /* Voucher keys are generated as uppercase alphanumerics with no separators,
           so normalise whatever was typed off the printed voucher. */
        const searchedKey = state.voucherSearchKey.replace(/\s/g, "").toUpperCase();

        dispatch(setLookupError(null));
        dispatch(setLookupResult(null));

        if (searchedKey.length === 0) return;

        dispatch(setLookupLoadingState(true));

        try {
            var companyService = useCompanyService();
            var productService = useProductService();
            var voucherService = useVoucherService();
            var productKeyService = useProductKeyService();
            var userService = useUserService();

            var voucher = await voucherService.getVoucherByKeyAsync(searchedKey);

            if (voucher == null) {
                dispatch(setLookupResult(new VoucherLookupResult(searchedKey, null, null, null, null, null, null, null, [])));
                dispatch(setLookupLoadingState(false));
                return;
            }

            var warnings: string[] = [];

            var company: Company | null = voucher.companyGuid == null ? null : await companyService.getCompanyByGuidAsync(voucher.companyGuid);
            var product: Product | null = voucher.productGuid == null ? null : await productService.getProductByGuidAsync(voucher.productGuid);

            var redeemedBy: User | null = voucher.redeemedByGuid == null ? null : await userService.getUserByGuidAsync(voucher.redeemedByGuid);
            var productKey: ProductKey | null = await productKeyService.getProductKeyByVoucherGuidAsync(voucher.guid);

            /* A key transferred in from another product's stock carries its origin,
               so support can see whose keys were actually handed out. */
            var productKeyOriginCompany: Company | null = productKey?.originCompanyGuid == null ? null : await companyService.getCompanyByGuidAsync(productKey.originCompanyGuid);
            var productKeyOriginProduct: Product | null = productKey?.originProductGuid == null ? null : await productService.getProductByGuidAsync(productKey.originProductGuid);

            if (voucher.companyGuid == null) {
                warnings.push("Voucher has no companyGuid.");
            } else if (company == null) {
                warnings.push(`No company found for companyGuid ${voucher.companyGuid}.`);
            }

            if (voucher.productGuid == null) {
                warnings.push("Voucher has no productGuid, so redemption can never find a product key for it.");
            } else if (product == null) {
                warnings.push(`No product found for productGuid ${voucher.productGuid}.`);
            }

            if (product != null && (product.emailTemplate == null || product.emailTemplate.length === 0)) {
                warnings.push("Product has no email template, so redemption will fail when it tries to send the email.");
            }

            if (voucher.redeembedDate != null && voucher.redeemedByGuid == null) {
                warnings.push("Voucher is marked redeemed but has no redeemedByGuid.");
            }

            if (voucher.redeemedByGuid != null && redeemedBy == null) {
                warnings.push(`No user found for redeemedByGuid ${voucher.redeemedByGuid}.`);
            }

            if (voucher.redeembedDate != null && productKey == null) {
                warnings.push("Voucher is marked redeemed but no product key is linked to it, so the customer cannot be given their key again.");
            }

            if (voucher.redeembedDate == null && productKey != null) {
                warnings.push("A product key is linked to this voucher even though the voucher is not marked redeemed.");
            }

            if (productKey != null && productKey.deletedDate != null) {
                warnings.push("The linked product key is marked deleted.");
            }

            if (voucher.deletedDate != null) {
                warnings.push("This voucher is marked deleted.");
            }

            dispatch(setLookupResult(new VoucherLookupResult(searchedKey, voucher, company, product, redeemedBy, productKey, productKeyOriginCompany, productKeyOriginProduct, warnings)));
        } catch (error) {
            dispatch(setLookupError(`${error}`));
        }

        dispatch(setLookupLoadingState(false));
        return;
    }
);

export const {
    setLoadingState,
    setSummaries,
    setLoadError,
    setVoucherSearchKey,
    setLookupLoadingState,
    setLookupResult,
    setLookupError,
} = ReportingPageSlice.actions;

export default ReportingPageSlice.reducer;

export const selectReportState = (state: RootState) => state.reportingPage;
